import React, { useState, useCallback, useRef, useEffect } from 'react';
import { HelpCircle, ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useInlineHelp } from '@/components/ui/inline-help-provider';
import { helpRegistry, resolveHelpEntry, type HelpEntry } from '@/components/ui/help-registry';
import { HelpSheetBody } from '@/components/ui/help-sheet-body';

// Match the sheet's close animation duration (data-[state=closed]:duration-300)
const SHEET_CLOSE_MS = 310;

// Globals for cross-HelpButton coordination.
// __pendingHelpOpen: field name the next-mounted HelpButton should auto-open for
// __helpSheetWasOpen: set when a HelpButton unmounts while its sheet is open,
//   so the next same-category HelpButton can auto-open
const _win = window as unknown as Record<string, unknown>;

interface HelpButtonProps {
  fieldName: string;
  content: string;
  variables?: Record<string, unknown>;
  enableFieldFocus?: boolean;
  variant?: 'icon' | 'link';
  className?: string;
  injectableComponents?: Record<string, React.ReactNode>;
}

export const HelpButton: React.FC<HelpButtonProps> = ({
  fieldName,
  content,
  variables,
  enableFieldFocus = false,
  variant = 'icon',
  className,
  injectableComponents,
}) => {
  const [open, setOpen] = useState(false);
  const [stack, setStack] = useState<Array<{ fieldName: string } & HelpEntry>>([]);
  const stackRef = useRef(stack);
  stackRef.current = stack;
  const [contentVisible, setContentVisible] = useState(true);
  const isMobile = useIsMobile();
  const inlineHelp = useInlineHelp();

  // Register this help content in the global registry
  const entryRef = useRef<HelpEntry>({ content, variables, injectableComponents });
  entryRef.current = { content, variables, injectableComponents };
  const openRef = useRef(open);
  openRef.current = open;

  useEffect(() => {
    helpRegistry.set(fieldName, entryRef);

    // Auto-open if this HelpButton was mounted due to a method switch.
    // Two signals: __pendingHelpOpen (in-sheet nav) or __helpSheetWasOpen
    // (config form switch where the previous HelpButton set it on unmount).
    //
    // When another sheet was open, the old Radix Sheet element plays an exit
    // animation. We delay our open by SHEET_CLOSE_MS so the enter animation
    // starts AFTER the exit finishes — giving a sequential close→open feel
    // instead of two overlapping sheets.
    const doOpen = () => {
      setContentVisible(true);
      setOpen(true);
    };

    const timers: ReturnType<typeof setTimeout>[] = [];
    const schedule = (fn: () => void, ms: number) => { timers.push(setTimeout(fn, ms)); };

    const tryAutoOpen = (deferred = false) => {
      if (_win.__pendingHelpOpen === fieldName) {
        delete _win.__pendingHelpOpen;
        delete _win.__helpSheetWasOpen;
        _win.__helpAutoOpenClaimed = true;
        schedule(() => { delete _win.__helpAutoOpenClaimed; }, SHEET_CLOSE_MS + 50);
        // If the exit animation already played (navigateHelp/closeHelpSheet
        // triggered it and the setTimeout waited for it), open immediately.
        // Otherwise delay by SHEET_CLOSE_MS to let the exit finish.
        const skipDelay = deferred || !!_win.__exitAlreadyPlayed;
        delete _win.__exitAlreadyPlayed;
        if (skipDelay) { doOpen(); } else { schedule(doOpen, SHEET_CLOSE_MS); }
        return true;
      }
      if (_win.__helpSheetWasOpen && !_win.__pendingHelpOpen && !_win.__helpAutoOpenClaimed) {
        delete _win.__helpSheetWasOpen;
        const skipDelay = deferred || !!_win.__exitAlreadyPlayed;
        delete _win.__exitAlreadyPlayed;
        if (skipDelay) { doOpen(); } else { schedule(doOpen, SHEET_CLOSE_MS); }
        return true;
      }
      return false;
    };

    // With AnimatePresence mode="sync", the new panel mounts BEFORE the old
    // one unmounts. The old HelpButton sets __helpSheetWasOpen in its cleanup,
    // which hasn't run yet. Re-check after exit animations complete.
    if (!tryAutoOpen()) {
      schedule(() => tryAutoOpen(true), 350);
    }

    return () => {
      for (const t of timers) clearTimeout(t);
      helpRegistry.delete(fieldName);
      // If this HelpButton unmounts while its sheet is open (e.g. config form
      // method switch), signal the next HelpButton to auto-open — unless an
      // in-sheet nav already set __pendingHelpOpen (that takes priority).
      if (openRef.current && !_win.__pendingHelpOpen && !_win.__helpAutoOpenClaimed) {
        _win.__helpSheetWasOpen = true;
        // Clear after a short window so unrelated HelpButtons don't pick it up.
        requestAnimationFrame(() => {
          setTimeout(() => { delete _win.__helpSheetWasOpen; }, 200);
        });
      }
    };
  }, [fieldName]);

  // Current view — stack top or the original props
  const currentView = stack.length > 0
    ? stack[stack.length - 1]
    : { fieldName, content, variables, injectableComponents };

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // On mobile inside a parent sheet, navigate inline instead of opening a second sheet
    if (isMobile && inlineHelp) {
      inlineHelp.showHelp(fieldName, content, variables, injectableComponents);
      return;
    }
    if (open) return;
    setContentVisible(true);
    setOpen(true);
  }, [open, isMobile, inlineHelp, fieldName, content, variables, injectableComponents]);

  // Navigation refs — stash the target so the close effect can reopen immediately
  const pendingNav = useRef<{ fieldName: string } & HelpEntry | null>(null);
  const pendingBack = useRef(false);

  // On close: if a navigation is pending, wait for the exit animation then
  // reopen with new content. This matches the ? button behavior — the sheet
  // slides out, content changes offscreen, then slides back in.
  useEffect(() => {
    if (open) return;
    if (pendingNav.current) {
      const entry = pendingNav.current;
      pendingNav.current = null;
      if (_win.__pendingMethodSwitch) {
        delete _win.__pendingMethodSwitch;
        setStack([]);
        setContentVisible(true);
        return;
      }
      const timer = setTimeout(() => {
        setStack(prev => [...prev, entry]);
        setContentVisible(true);
        setOpen(true);
      }, SHEET_CLOSE_MS);
      return () => clearTimeout(timer);
    }
    if (pendingBack.current) {
      pendingBack.current = false;
      const timer = setTimeout(() => {
        setStack(prev => prev.slice(0, -1));
        setContentVisible(true);
        setOpen(true);
      }, SHEET_CLOSE_MS);
      return () => clearTimeout(timer);
    }
    if (_win.__pendingMethodSwitch) {
      delete _win.__pendingMethodSwitch;
    }
    setStack([]);
    setContentVisible(true);
  }, [open]);

  // Expose closeHelpSheet so external code (e.g. config form radio clicks)
  // can trigger the exit animation before the HelpButton unmounts.
  useEffect(() => {
    if (!open) return;
    _win.closeHelpSheet = () => {
      setContentVisible(false);
      setOpen(false);
    };
    return () => { delete _win.closeHelpSheet; };
  }, [open]);

  // Expose navigateHelp when sheet is open — close then immediately reopen
  useEffect(() => {
    if (!open) return;
    _win.navigateHelp = (targetFieldName: string) => {
      const entry = resolveHelpEntry(targetFieldName);
      if (!entry) return;
      const s = stackRef.current;
      const currentName = s.length > 0 ? s[s.length - 1].fieldName : fieldName;
      if (targetFieldName === currentName) return;
      pendingNav.current = { fieldName: targetFieldName, ...entry };
      setOpen(false);
    };
    return () => { delete _win.navigateHelp; };
  }, [open, fieldName]);

  const handleBack = useCallback(() => {
    pendingBack.current = true;
    setOpen(false);
  }, []);

  const side = isMobile ? 'bottom' : 'right';

  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={handleClick}
          className={cn(
            'inline-flex items-center justify-center touch-target-pseudo text-muted-foreground hover:text-primary-text cursor-pointer transition-colors',
            className
          )}
          aria-label={`Learn more about ${fieldName}`}
        >
          <HelpCircle aria-hidden="true" className="w-3.5 h-3.5" />
        </button>
      ) : (
        <button
          type="button"
          onClick={handleClick}
          className={cn(
            'inline-flex items-center gap-1 text-primary hover:text-primary/80 cursor-pointer text-xs font-medium py-2 -my-2',
            className
          )}
        >
          Learn more
          <HelpCircle aria-hidden="true" className="w-3 h-3" />
        </button>
      )}
      <Sheet open={open} onOpenChange={setOpen} modal={false}>
        <SheetContent
          side={side}
          onClose={() => setOpen(false)}
          dragHandleVariant="header"
          className={cn(
            'flex flex-col overflow-hidden bg-[var(--color-surface-1)] panel-bevel rounded',
            side === 'right' && 'sm:max-w-lg !bottom-auto !h-auto max-h-[90vh] !top-1/2 !-translate-y-1/2 !right-1.5 border-l-2 border-l-primary/20',
            side === 'bottom' && 'max-h-[70vh] border-t-2 border-t-primary/20 pb-[max(1rem,env(safe-area-inset-bottom))]'
          )}
          aria-describedby={undefined}
        >
          {/* Visually hidden — satisfies Radix Dialog accessibility requirement.
              The visible title is rendered by HelpSheetBody as a plain <h2>. */}
          <SheetTitle className="sr-only">{currentView.fieldName}</SheetTitle>
          <HelpSheetBody
            fieldName={currentView.fieldName}
            content={currentView.content}
            variables={currentView.variables}
            injectableComponents={currentView.injectableComponents}
            enableFieldFocus={stack.length === 0 ? enableFieldFocus : false}
            contentVisible={contentVisible}
            headerLeading={
              stack.length > 0 ? (
                <button
                  type="button"
                  onClick={handleBack}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground cursor-pointer transition-colors self-start"
                >
                  <ArrowLeft className="w-3 h-3" />
                  Back
                </button>
              ) : undefined
            }
          />
        </SheetContent>
      </Sheet>
    </>
  );
};
